// Página WarehouseForm - Formulario para crear/editar depósitos
// Página completa para gestionar depósitos

import { useState, useEffect, useCallback } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { getWarehouse, createWarehouse, updateWarehouse } from '../../services/warehouses.service'
import Button from '../../components/Button'
import Toast from '../../components/Toast'
import './WarehouseForm.css'

/**
 * Página WarehouseForm
 * Formulario completo para crear o editar depósitos
 */
function WarehouseForm() {
  const navigate = useNavigate()
  const { id } = useParams()
  const isEditing = !!id

  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [notification, setNotification] = useState(null)

  // Estado del formulario
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    phone: '',
    email: '',
    active: true,
    notes: ''
  })

  const fetchWarehouseData = useCallback(async () => {
    try {
      setLoading(true)
      const warehouse = await getWarehouse(id)
      
      if (warehouse) {
        setFormData({
          name: warehouse.name || '',
          address: warehouse.address || '',
          phone: warehouse.phone || '',
          email: warehouse.email || '',
          active: warehouse.active !== undefined ? warehouse.active : true,
          notes: warehouse.notes || ''
        })
      }
    } catch (error) {
      console.error('Error cargando depósito:', error)
      setNotification({
        type: 'error',
        message: 'Error al cargar los datos del depósito'
      })
      setTimeout(() => setNotification(null), 3000)
    } finally {
      setLoading(false)
    }
  }, [id])

  useEffect(() => {
    if (isEditing) {
      fetchWarehouseData()
    }
  }, [isEditing, fetchWarehouseData])

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (saving) return

    // Validación básica
    if (!formData.name.trim()) {
      setNotification({
        type: 'error',
        message: 'El nombre del depósito es requerido'
      })
      setTimeout(() => setNotification(null), 3000)
      return
    }

    setSaving(true)

    try {
      if (isEditing) {
        // Actualizar depósito existente
        await updateWarehouse(id, formData)
        
        setNotification({
          type: 'success',
          message: 'Depósito actualizado correctamente'
        })
      } else {
        // Crear nuevo depósito
        await createWarehouse(formData)
        
        setNotification({
          type: 'success',
          message: `Depósito "${formData.name}" creado correctamente`
        })
      }
      
      setTimeout(() => {
        navigate('/admin/depositos')
      }, 1500)
    } catch (error) {
      console.error('Error guardando depósito:', error)
      setNotification({
        type: 'error',
        message: `Error al guardar depósito: ${error.message || 'Error desconocido'}`
      })
      setTimeout(() => setNotification(null), 5000)
      setSaving(false)
    }
  }

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))
  }

  if (loading) {
    return (
      <div className="warehouse-form-page">
        <div className="loading-state">
          <p>Cargando datos del depósito...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="warehouse-form-page">
      <Toast
        isVisible={!!notification}
        message={notification?.message || ''}
        type={notification?.type || 'error'}
        onClose={() => setNotification(null)}
      />

      <div className="form-header">
        <div>
          <button
            className="back-button"
            onClick={() => navigate('/admin/depositos')}
          >
            ← Volver
          </button>
          <h1>{isEditing ? 'Editar Depósito' : 'Crear Nuevo Depósito'}</h1>
          <p className="form-subtitle">
            {isEditing 
              ? 'Modifica la información del depósito'
              : 'Completa los datos para crear un nuevo depósito o sucursal'}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="warehouse-form">
        <div className="form-section">
          <h2 className="section-title">Información Básica</h2>
          
          <div className="form-group">
            <label htmlFor="name">
              Nombre del Depósito <span className="required">*</span>
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Ej: Depósito Central, Sucursal Norte"
              required
              disabled={saving}
              className="form-input"
            />
          </div>

          <div className="form-group">
            <label htmlFor="address">Dirección</label>
            <input
              type="text"
              id="address"
              name="address"
              value={formData.address}
              onChange={handleChange}
              placeholder="Calle, número, ciudad"
              disabled={saving}
              className="form-input"
            />
          </div>
        </div>

        <div className="form-section">
          <h2 className="section-title">Información de Contacto</h2>
          
          <div className="form-group">
            <label htmlFor="phone">Teléfono</label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder="+54 9 11 1234-5678"
              disabled={saving}
              className="form-input"
            />
          </div>

          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="deposito@pinohome.com"
              disabled={saving}
              className="form-input"
            />
          </div>
        </div>

        <div className="form-section">
          <h2 className="section-title">Configuración</h2>
          
          <div className="form-group checkbox-group">
            <label>
              <input
                type="checkbox"
                name="active"
                checked={formData.active}
                onChange={handleChange}
                disabled={saving}
              />
              <span>Depósito activo</span>
            </label>
            <small className="form-hint">
              Los depósitos inactivos no aparecerán en las listas de selección
            </small>
          </div>

          <div className="form-group">
            <label htmlFor="notes">Notas / Observaciones</label>
            <textarea
              id="notes"
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              placeholder="Información adicional sobre el depósito..."
              rows={4}
              disabled={saving}
              className="form-input"
            />
          </div>
        </div>

        {/* Botones de acción */}
        <div className="form-actions">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/admin/depositos')}
            disabled={saving}
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={saving}
          >
            {saving ? 'Guardando...' : (isEditing ? 'Actualizar Depósito' : 'Crear Depósito')}
          </Button>
        </div>
      </form>
    </div>
  )
}

export default WarehouseForm

